import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Loader2, AlertTriangle } from 'lucide-react';
import { diagnosticsAPI } from '../../lib/api';

export function FxBlastRadiusPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const run = () => {
    setLoading(true);
    setError(null);
    diagnosticsAPI.fxBlastRadius()
      .then(({ data: d }) => setData(d))
      .catch((e) => setError(e?.response?.data?.detail || 'Failed to load blast-radius data.'))
      .finally(() => setLoading(false));
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">FX Blast Radius</CardTitle>
        </div>
        <p className="text-xs text-muted-foreground">
          Documents created during the stale-FX window (since {data?.window_start || '2026-05-19'}) whose
          stored fx_rate matches a FALLBACK_RATES value exactly. Non-zero means invoices captured a hardcoded
          rate instead of a live API rate.
        </p>
      </CardHeader>
      <CardContent>
        {!data && !loading && (
          <div className="space-y-2">
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button size="sm" variant="outline" onClick={run}>
              {error ? 'Retry' : 'Run Blast-Radius Query'}
            </Button>
          </div>
        )}
        {loading && (
          <div className="flex items-center gap-2 py-4">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm text-muted-foreground">Querying invoices and transactions...</span>
          </div>
        )}
        {data && (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Badge variant={data.total === 0 ? 'default' : 'destructive'} className="text-xs">
                {data.total === 0 ? 'Clean' : `${data.total} affected`}
              </Badge>
              <span className="text-xs text-muted-foreground">
                {data.invoices.count} invoices, {data.transactions.count} transactions
              </span>
              <Button size="sm" variant="ghost" onClick={run} className="ml-auto h-6 text-xs">
                Re-run
              </Button>
            </div>
            <p className="text-xs">{data.assessment}</p>

            {data.invoices.count > 0 && (
              <div className="border rounded overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left px-2 py-1 font-medium">ID</th>
                      <th className="text-left px-2 py-1 font-medium">Source</th>
                      <th className="text-left px-2 py-1 font-medium">Vendor</th>
                      <th className="text-right px-2 py-1 font-medium">Amount</th>
                      <th className="text-left px-2 py-1 font-medium">Ccy</th>
                      <th className="text-right px-2 py-1 font-medium">fx_rate</th>
                      <th className="text-left px-2 py-1 font-medium">fx_date</th>
                      <th className="text-center px-2 py-1 font-medium">Review</th>
                      <th className="text-center px-2 py-1 font-medium">Match</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.invoices.sample.map((d, i) => (
                      <tr key={d.id || i} className="border-b last:border-0">
                        <td className="px-2 py-1 font-mono truncate max-w-[120px]" title={d.id}>{d.id?.slice(0, 12) || d.external_id?.slice(0, 12) || '-'}</td>
                        <td className="px-2 py-1">{d.source || '-'}</td>
                        <td className="px-2 py-1 truncate max-w-[140px]" title={d.vendor}>{d.vendor || '-'}</td>
                        <td className="px-2 py-1 text-right font-mono">{d.amount != null ? Number(d.amount).toFixed(2) : '-'}</td>
                        <td className="px-2 py-1">{d.currency || '-'}</td>
                        <td className="px-2 py-1 text-right font-mono">{d.fx_rate}</td>
                        <td className="px-2 py-1">{d.fx_date || '-'}</td>
                        <td className="px-2 py-1 text-center">
                          <Badge variant={d.review_status === 'approved' ? 'default' : 'secondary'} className="text-[10px]">
                            {d.review_status || '-'}
                          </Badge>
                        </td>
                        <td className="px-2 py-1 text-center">
                          <Badge variant={d.matching_status === 'matched' ? 'default' : 'secondary'} className="text-[10px]">
                            {d.matching_status || '-'}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {data.invoices.count > data.invoices.sample.length && (
                  <p className="text-xs text-muted-foreground px-2 py-1">
                    Showing {data.invoices.sample.length} of {data.invoices.count} invoices.
                  </p>
                )}
              </div>
            )}

            {data.transactions.count > 0 && (
              <div className="border rounded overflow-x-auto">
                <p className="text-xs font-medium px-2 py-1 bg-muted/50">Transactions</p>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left px-2 py-1 font-medium">ID</th>
                      <th className="text-left px-2 py-1 font-medium">Source</th>
                      <th className="text-right px-2 py-1 font-medium">Amount</th>
                      <th className="text-left px-2 py-1 font-medium">Ccy</th>
                      <th className="text-right px-2 py-1 font-medium">fx_rate</th>
                      <th className="text-left px-2 py-1 font-medium">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.transactions.sample.map((d, i) => (
                      <tr key={d.id || i} className="border-b last:border-0">
                        <td className="px-2 py-1 font-mono truncate max-w-[120px]" title={d.id}>{d.id?.slice(0, 12) || d.external_id?.slice(0, 12) || '-'}</td>
                        <td className="px-2 py-1">{d.source || '-'}</td>
                        <td className="px-2 py-1 text-right font-mono">{d.amount != null ? Number(d.amount).toFixed(2) : '-'}</td>
                        <td className="px-2 py-1">{d.currency || '-'}</td>
                        <td className="px-2 py-1 text-right font-mono">{d.fx_rate}</td>
                        <td className="px-2 py-1">{d.date || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {data.transactions.count > data.transactions.sample.length && (
                  <p className="text-xs text-muted-foreground px-2 py-1">
                    Showing {data.transactions.sample.length} of {data.transactions.count} transactions.
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
